import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { dropTask } from 'ember-concurrency';
import { t } from 'ember-intl';
import { trackedFunction } from 'reactiveweb/function';
import { PhCheck, PhCheckCircle } from 'ember-phosphor-icons';
import Button from 'tio-ui/components/button';
import Card from 'tio-ui/components/card';
import Chip from 'tio-ui/components/chip';
import PageHeader from 'tio-ui/components/page-header';
import ProgressBar from 'tio-ui/components/progress-bar';
import Text from 'tio-ui/components/text';
import formatCentsToDollars from 'tio-common/helpers/format-cents-to-dollars';
import HeldLoanCard from 'tio-employee/components/slr/loan-verification/held-loan-card';
import LoanMeta from 'tio-employee/components/slr/loan-verification/loan-meta';
import {
  readLoanIdentityRefusal,
  type LoanIdentityDecision,
} from 'tio-data/requests/loan-identity-answer';
import {
  anotherAccountWaits,
  becomesBalance,
  decisionsFor,
  keepValidChoices,
  pendingHeldLoans,
  recordedHeldLoans,
  unavailableLoanIds,
  type LoanChoice,
} from 'tio-employee/utils/slr/loan-verification';
import type { ExistingLoan, LoanVerification } from 'tio-data/requests/loan-verification';
import type EmployeeService from 'tio-employee/services/employee';
import type MemberResolutionsService from 'tio-employee/services/member-resolutions';
import type { IntlService } from 'ember-intl';

type PageNotice = 'stale' | 'stale-updated' | 'failed';

type Loaded = { verification: LoanVerification } | { failure: 'closed' | 'failed' };

interface Submission {
  verification: LoanVerification;
  choices: Record<string, LoanChoice>;
  decisions: LoanIdentityDecision[];
}

// Everything the member did on one question; a new `resolutionId` starts from a fresh one.
class QuestionState {
  @tracked choices: Record<string, LoanChoice> = {};
  @tracked pickerHoldId: string | null = null;
  @tracked notice: PageNotice | null = null;
  @tracked refusedHoldIds: string[] = [];
  @tracked submission: Submission | null = null;

  constructor(readonly resolutionId: string) {}
}

function requestStatus(error: unknown) {
  return readLoanIdentityRefusal(error).status;
}

interface LoanVerificationPageSignature {
  Args: {
    /** The question the alert opened; the page serves every held loan in its group. */
    resolutionId: string;
    /** `documents` sends the back link to the documents list; anything else to the SLR dashboard. */
    from?: string;
  };
}

/**
 * The member's full-page "Verify Your Loans" flow for one uploaded document and account: each
 * held loan is added as new or replaces exactly one existing loan (strict 1:1), and "Submit
 * Verification" records every decision at once; the upload's next reflection applies them. The
 * confirmation lives for the page session only; nothing can be changed once submitted. Every
 * amount and preview is the server's, in cents.
 */
export default class LoanVerificationPage extends Component<LoanVerificationPageSignature> {
  @service('member-resolutions') declare memberResolutions: MemberResolutionsService;
  @service declare employee: EmployeeService;
  @service declare intl: IntlService;

  @tracked showsExistingLoans = false;

  @cached
  get question() {
    return new QuestionState(this.args.resolutionId);
  }

  get choices() {
    return this.question.choices;
  }

  get notice() {
    return this.question.notice;
  }

  get submission() {
    return this.question.submission;
  }

  loaded = trackedFunction(this, async (): Promise<Loaded> => {
    try {
      return {
        verification: await this.memberResolutions.loanVerification(this.args.resolutionId),
      };
    } catch (error) {
      return { failure: requestStatus(error) === 404 ? 'closed' : 'failed' };
    }
  });

  get verification() {
    const loaded = this.loaded.value;
    return loaded && 'verification' in loaded ? loaded.verification : null;
  }

  get isLoading() {
    return !this.loaded.value;
  }

  get loadFailed() {
    const loaded = this.loaded.value;
    return !!loaded && 'failure' in loaded && loaded.failure === 'failed';
  }

  // Closed: the server no longer serves the question, or every loan on it is already applied.
  get isClosed() {
    if (this.submission) return false;
    const loaded = this.loaded.value;
    if (loaded && 'failure' in loaded) return loaded.failure === 'closed';
    return (
      !!this.verification &&
      pendingHeldLoans(this.verification).length === 0 &&
      recordedHeldLoans(this.verification).length === 0
    );
  }

  // Nothing is left to answer, but the upload has not applied every recorded answer yet.
  get isRecorded() {
    if (this.submission || this.isClosed || !this.verification) return false;
    return (
      pendingHeldLoans(this.verification).length === 0 &&
      recordedHeldLoans(this.verification).length > 0
    );
  }

  // Answers wait on the upload's other accounts too, so "shortly" would not hold.
  get waitsOnAnotherAccount() {
    const verification = this.submission?.verification ?? this.verification;
    return (
      !!verification &&
      anotherAccountWaits(
        this.employee.slrAlertsData.value ?? [],
        verification,
        (question) => !!this.memberResolutions.answerFor(question)
      )
    );
  }

  get servicerName() {
    return this.verification?.servicerName ?? null;
  }

  get activeChoices() {
    return this.verification ? keepValidChoices(this.verification, this.choices) : {};
  }

  get totalCount() {
    return this.verification?.heldLoans.length ?? 0;
  }

  get reviewedCount() {
    const choices = this.activeChoices;
    return (this.verification?.heldLoans ?? []).filter(
      (loan) => loan.state !== 'held' || !!choices[loan.holdId]
    ).length;
  }

  get undecidedCount() {
    if (!this.verification) return 0;
    const choices = this.activeChoices;
    return pendingHeldLoans(this.verification).filter((loan) => !choices[loan.holdId]).length;
  }

  get decisions() {
    return this.verification ? decisionsFor(this.verification, this.activeChoices) : null;
  }

  get isSubmitDisabled() {
    return !this.decisions || this.submit.isRunning;
  }

  get backRoute() {
    return this.args.from === 'documents'
      ? 'authenticated.account-activity.accounts'
      : 'authenticated.slr.dashboard';
  }

  get backLabel() {
    return this.intl.t(
      this.args.from === 'documents'
        ? 'slr.loan_verification.back_to_documents'
        : 'slr.loan_verification.back_to_dashboard'
    );
  }

  get crumbLabel() {
    return this.intl.t(this.args.from === 'documents' ? 'accounts.my_documents' : 'dashboard');
  }

  get pageTitle() {
    return this.intl.t(
      this.submission ? 'slr.loan_verification.submitted_title' : 'slr.loan_verification.page_title'
    );
  }

  get noticeMessage() {
    if (!this.notice) return '';
    const settled = this.notice !== 'failed' && (this.isClosed || this.isRecorded);
    return this.intl.t(
      `slr.loan_verification.notice_${this.notice.replace('-', '_')}${settled ? '_settled' : ''}`
    );
  }

  get thanksMessage() {
    return this.intl.t(
      this.waitsOnAnotherAccount
        ? 'slr.loan_verification.thanks_other_account'
        : 'slr.loan_verification.thanks',
      { count: this.submission?.decisions.length ?? 0 }
    );
  }

  get recordedMessage() {
    return this.intl.t(
      this.waitsOnAnotherAccount
        ? 'slr.loan_verification.recorded_other_account'
        : 'slr.loan_verification.recorded'
    );
  }

  get submittedLoans() {
    const submission = this.submission;
    if (!submission) return [];
    const decided = new Set(submission.decisions.map((decision) => decision.holdId));
    return submission.verification.heldLoans.filter((loan) => decided.has(loan.holdId));
  }

  choiceFor = (holdId: string, choices: Record<string, LoanChoice>) => choices[holdId];

  unavailableFor = (holdId: string) =>
    this.verification
      ? unavailableLoanIds(this.verification, this.activeChoices, holdId)
      : new Set<string>();

  errorFor = (holdId: string) =>
    this.question.refusedHoldIds.includes(holdId)
      ? this.intl.t('slr.loan_verification.error_invalid')
      : undefined;

  isPickerOpenFor = (holdId: string) => this.question.pickerHoldId === holdId;

  isReadOnly = (state: string) => state !== 'held' || this.submit.isRunning;

  position = (index: number) => index + 1;

  // A loan picked on this page shows the balance it would carry; the rest show their own.
  existingBalance = (existing: ExistingLoan) => {
    const verification = this.verification;
    if (!verification) return existing.currentBalance;
    for (const [holdId, choice] of Object.entries(this.activeChoices)) {
      if (choice.kind === 'replace' && choice.existingLoanId === existing.loanId) {
        const held = verification.heldLoans.find((loan) => loan.holdId === holdId);
        return held
          ? (becomesBalance(held, existing.loanId) ?? existing.currentBalance)
          : existing.currentBalance;
      }
    }
    return existing.currentBalance;
  };

  isPicked = (existing: ExistingLoan) =>
    Object.values(this.activeChoices).some(
      (choice) => choice.kind === 'replace' && choice.existingLoanId === existing.loanId
    );

  #choose(holdId: string, choice: LoanChoice | null) {
    const question = this.question;
    const next = { ...this.activeChoices };
    if (choice) next[holdId] = choice;
    else delete next[holdId];
    question.choices = next;
    question.refusedHoldIds = question.refusedHoldIds.filter((id) => id !== holdId);
  }

  chooseNew = (holdId: string) => {
    this.question.pickerHoldId = null;
    this.#choose(holdId, { kind: 'new' });
  };

  openPicker = (holdId: string) => {
    this.question.pickerHoldId = holdId;
  };

  cancelPicker = () => {
    this.question.pickerHoldId = null;
  };

  chooseExisting = (holdId: string, existingLoanId: string) => {
    if (this.unavailableFor(holdId).has(existingLoanId)) return;
    this.question.pickerHoldId = null;
    this.#choose(holdId, { kind: 'replace', existingLoanId });
  };

  change = (holdId: string) => {
    this.#choose(holdId, null);
  };

  toggleExistingLoans = () => {
    this.showsExistingLoans = !this.showsExistingLoans;
  };

  submit = dropTask(async () => {
    const verification = this.verification;
    const decisions = this.decisions;
    if (!verification || !decisions) return;
    // The summary must show what was sent, so read the choices before anything can move them.
    const choices = this.activeChoices;
    const question = this.question;

    question.notice = null;
    question.refusedHoldIds = [];
    try {
      await this.memberResolutions.answerLoanIdentity(verification, decisions);
      question.submission = { verification, choices, decisions };
    } catch (error) {
      await this.#explainRefusal(question, error, decisions);
    }
  });

  async #explainRefusal(
    question: QuestionState,
    error: unknown,
    decisions: LoanIdentityDecision[]
  ) {
    const refusal = readLoanIdentityRefusal(error);
    // A 404 can mean only some named loans closed, so the re-read decides what is left.
    if (refusal.status === 404 || refusal.status === 409) {
      question.notice = refusal.discardedHoldIds.length ? 'stale-updated' : 'stale';
      await this.loaded.retry();
    } else if (refusal.status === 422) {
      const refused = refusal.fieldErrors.flatMap((field) =>
        field.index === null ? [] : (decisions[field.index]?.holdId ?? [])
      );
      question.refusedHoldIds = refused;
      if (!refused.length) question.notice = 'failed';
    } else {
      question.notice = 'failed';
    }
  }

  <template>
    <div class="tio-col w-full min-w-0 pii" data-test-loan-verification>
      <PageHeader @title={{this.pageTitle}} @sticky={{false}}>
        <:breadcrumbs as |b|>
          <b.crumb
            @route={{this.backRoute}}
            @label={{this.crumbLabel}}
            data-test-loan-verification-back
          />
        </:breadcrumbs>
      </PageHeader>

      {{#if this.submission}}
        <Card class="tio-col w-full" data-test-loan-verification-submitted>
          <div class="tio-row-center rounded-full size-12 tio-bg-success-soft">
            <PhCheckCircle @size={{28}} class="tio-fg-success" aria-hidden="true" />
          </div>
          <Text data-test-loan-verification-thanks>{{this.thanksMessage}}</Text>
          <div class="tio-col-tight w-full">
            {{#each this.submittedLoans as |loan index|}}
              <HeldLoanCard
                @loan={{loan}}
                @position={{this.position index}}
                @total={{this.submittedLoans.length}}
                @existingLoans={{this.submission.verification.existingLoans}}
                @choice={{this.choiceFor loan.holdId this.submission.choices}}
                @unavailableLoanIds={{this.unavailableFor loan.holdId}}
                @isPickerOpen={{false}}
                @isReadOnly={{true}}
              />
            {{/each}}
          </div>
          <div>
            <Button
              @intent="primary"
              @route={{this.backRoute}}
              @testHook="loan-verification__done"
              data-test-loan-verification-done
            >
              {{this.backLabel}}
            </Button>
          </div>
        </Card>
      {{else if this.isLoading}}
        <div
          class="animate-pulse tio-col w-full"
          aria-busy="true"
          data-test-loan-verification-loading
        >
          <div class="tio-bg-sunken rounded-lg h-4 w-2/3"></div>
          <div class="tio-bg-sunken rounded-lg h-24 w-full"></div>
          <div class="tio-bg-sunken rounded-lg h-32 w-full"></div>
        </div>
      {{else if this.isClosed}}
        <Card class="tio-col w-full" data-test-loan-verification-closed>
          {{#if this.notice}}
            <Text
              @size="sm"
              @variant={{if (eq this.notice "failed") "error" "warning"}}
              role="alert"
              data-test-loan-verification-notice={{this.notice}}
            >
              {{this.noticeMessage}}
            </Text>
          {{/if}}
          <Text>{{t "slr.loan_verification.closed"}}</Text>
        </Card>
      {{else if this.isRecorded}}
        <Card class="tio-col w-full" data-test-loan-verification-recorded>
          {{#if this.notice}}
            <Text
              @size="sm"
              @variant={{if (eq this.notice "failed") "error" "warning"}}
              role="alert"
              data-test-loan-verification-notice={{this.notice}}
            >
              {{this.noticeMessage}}
            </Text>
          {{/if}}
          <Text data-test-loan-verification-recorded-message>{{this.recordedMessage}}</Text>
        </Card>
      {{else if this.loadFailed}}
        <Card class="tio-col w-full" data-test-loan-verification-load-failed>
          <Text @variant="error" role="alert">{{t "slr.loan_verification.load_failed"}}</Text>
        </Card>
      {{else if this.verification}}
        <Text @variant="muted" data-test-loan-verification-intro>
          {{#if this.servicerName}}
            {{t "slr.loan_verification.intro" servicer=this.servicerName}}
          {{else}}
            {{t "slr.loan_verification.intro_unnamed"}}
          {{/if}}
        </Text>

        <Card class="tio-row-between flex-wrap w-full" data-test-loan-verification-progress>
          <div class="tio-col-extra-tight tio-fill-min">
            <Text @weight="semibold" data-test-loan-verification-progress-count>
              {{t
                "slr.loan_verification.progress"
                reviewed=this.reviewedCount
                total=this.totalCount
              }}
            </Text>
            <ProgressBar
              @value={{this.reviewedCount}}
              @max={{this.totalCount}}
              @color="primary"
              @label={{t
                "slr.loan_verification.progress"
                reviewed=this.reviewedCount
                total=this.totalCount
              }}
            />
          </div>
          <Button
            @appearance="outlined"
            @testHook="loan-verification__existing-toggle"
            aria-expanded={{if this.showsExistingLoans "true" "false"}}
            data-test-loan-verification-existing-toggle
            {{on "click" this.toggleExistingLoans}}
          >
            {{#if this.showsExistingLoans}}
              {{t "slr.loan_verification.hide_existing"}}
            {{else}}
              {{t "slr.loan_verification.show_existing"}}
            {{/if}}
          </Button>
        </Card>

        {{#if this.showsExistingLoans}}
          <Card class="tio-col-tight w-full" data-test-loan-verification-existing>
            <Text @weight="semibold">{{t "slr.loan_verification.existing_heading"}}</Text>
            {{#if this.servicerName}}
              <Text @size="sm" @variant="muted">
                {{t "slr.loan_verification.existing_subtitle" servicer=this.servicerName}}
              </Text>
            {{/if}}
            {{#each this.verification.existingLoans as |existing|}}
              <div
                class="tio-row-start-between w-full"
                data-test-loan-verification-existing-loan={{existing.loanId}}
              >
                <div class="tio-col-extra-tight tio-fill-min">
                  <Text @size="sm" @weight="medium">{{existing.name}}</Text>
                  <Text @size="xs" @variant="muted">
                    <LoanMeta
                      @label={{or existing.typeLabel ""}}
                      @disbursedOn={{existing.disbursedOn}}
                    />
                  </Text>
                  {{#if (this.isPicked existing)}}
                    <div><Chip @intent="success" @size="sm">{{t
                          "slr.loan_verification.updated_tag"
                        }}</Chip></div>
                  {{else if existing.inUse}}
                    <div><Chip @size="sm">{{t "slr.loan_verification.in_use"}}</Chip></div>
                  {{/if}}
                </div>
                <Text @size="sm" @weight="semibold">
                  {{formatCentsToDollars (this.existingBalance existing)}}
                </Text>
              </div>
            {{/each}}
          </Card>
        {{/if}}

        {{#if this.notice}}
          <Text
            @size="sm"
            @variant={{if (eq this.notice "failed") "error" "warning"}}
            role="alert"
            data-test-loan-verification-notice={{this.notice}}
          >
            {{this.noticeMessage}}
          </Text>
        {{/if}}

        <div class="tio-col-tight w-full" data-test-loan-verification-loans>
          {{#each this.verification.heldLoans as |loan index|}}
            <HeldLoanCard
              @loan={{loan}}
              @position={{this.position index}}
              @total={{this.totalCount}}
              @existingLoans={{this.verification.existingLoans}}
              @choice={{this.choiceFor loan.holdId this.activeChoices}}
              @unavailableLoanIds={{this.unavailableFor loan.holdId}}
              @isPickerOpen={{this.isPickerOpenFor loan.holdId}}
              @isReadOnly={{this.isReadOnly loan.state}}
              @errorMessage={{this.errorFor loan.holdId}}
              @onChooseNew={{fn this.chooseNew loan.holdId}}
              @onOpenPicker={{fn this.openPicker loan.holdId}}
              @onCancelPicker={{this.cancelPicker}}
              @onChooseExisting={{fn this.chooseExisting loan.holdId}}
              @onChange={{fn this.change loan.holdId}}
            />
          {{/each}}
        </div>

        <Card class="tio-row-between flex-wrap w-full" data-test-loan-verification-footer>
          <Text @size="sm" data-test-loan-verification-status>
            {{#if this.decisions}}
              {{t "slr.loan_verification.ready"}}
            {{else}}
              {{t "slr.loan_verification.review_all" total=this.undecidedCount}}
            {{/if}}
          </Text>
          <Button
            @intent="primary"
            @icon={{PhCheck}}
            @iconPlacement="end"
            @isDisabled={{this.isSubmitDisabled}}
            @isRunning={{this.submit.isRunning}}
            @testHook="loan-verification__submit"
            data-test-loan-verification-submit
            {{on "click" this.submit.perform}}
          >
            {{t "slr.loan_verification.submit"}}
          </Button>
        </Card>
      {{/if}}
    </div>
  </template>
}
